import { useMemo, useRef } from 'react';

type ClassValue = string | undefined | null | false | Record<string, boolean>;

export const cx = (...classes: ClassValue[]): string =>
  classes
    .flatMap(c => {
      if (!c) {
        return null;
      }
      if (typeof c === 'string') {
        return c;
      }
      return Object.keys(c).filter(key => c[key]);
    })
    .filter(c => !!c)
    .join(' ');

export function randomString(): string {
  return (Math.random() + 1).toString(36).substring(7);
}

// Drops calls made while a previous (possibly async) call is still running, e.g. double clicks.
export function useSafeHandler<A extends unknown[], R>(
  handler: ((...args: A) => R) | undefined,
): ((...args: A) => Promise<R | undefined>) | undefined {
  const processing = useRef(false);
  return useMemo(() => {
    if (!handler) {
      return undefined;
    }
    return async (...args: A) => {
      if (processing.current) {
        return undefined;
      }
      processing.current = true;
      try {
        return await handler(...args);
      } finally {
        processing.current = false;
      }
    };
  }, [handler]);
}
